import React, { useState } from 'react';
import CardCarousel from '../cards/CardCarousel';
import CardBenefitsPanel from '../cards/CardBenefitsPanel';
import MerchantLookup from '../MerchantLookup';
import RecommendationResults from '../RecommendationResults';
import BentoCard from '../ui/BentoCard';
import LoadingSpinner from '../ui/LoadingSpinner';

const QUICK_MERCHANTS = [
  'Amazon', 'Starbucks', 'Whole Foods', 'Target', 'Costco',
  'Uber', 'DoorDash', 'Apple', 'Nike', 'Sephora',
];

function CardsTab({
  cards,
  onAddCard,
  onDeleteCard,
  onMerchantLookup,
  recommendations,
  loading,
  merchantData,
  searchHistory,
  onHistoryItemClick,
  isAuthenticated,
  // Benefits props
  benefitsData,
  benefitsLoading,
  benefitsError,
  onRefreshBenefits,
}) {
  const [showAllMerchants, setShowAllMerchants] = useState(false);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div className="pt-2">
        <h1 className="text-2xl font-bold text-on-surface tracking-display">Cards</h1>
        <p className="text-sm text-on-surface-variant mt-1.5">Manage your wallet & maximize rewards</p>
      </div>

      {/* Card Carousel */}
      <BentoCard>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-semibold text-on-surface">My Cards ({cards.length})</h2>
        </div>
        <CardCarousel cards={cards} onAddCard={onAddCard} onDeleteCard={onDeleteCard} />
      </BentoCard>

      {/* Your Card Benefits — authenticated users only */}
      {isAuthenticated && cards.length > 0 && (
        <CardBenefitsPanel
          benefitsData={benefitsData}
          loading={benefitsLoading}
          error={benefitsError}
          onRefresh={onRefreshBenefits}
        />
      )}

      {/* Quick Find Merchant */}
      <div>
        <h2 className="text-base font-semibold text-on-surface mb-3">Quick Find</h2>
        <div className="flex flex-wrap gap-2">
          {(showAllMerchants ? QUICK_MERCHANTS : QUICK_MERCHANTS.slice(0, 6)).map((merchant) => (
            <button
              key={merchant}
              onClick={() => onHistoryItemClick(merchant)}
              className="px-3.5 py-2 bg-surface-container-low rounded-full text-sm font-medium text-on-surface-variant hover:bg-primary-light hover:text-primary transition-colors"
            >
              {merchant}
            </button>
          ))}
          {!showAllMerchants && (
            <button
              onClick={() => setShowAllMerchants(true)}
              className="px-3.5 py-2 bg-surface-container-low rounded-full text-sm font-medium text-primary"
            >
              +{QUICK_MERCHANTS.length - 6} more
            </button>
          )}
        </div>
      </div>

      {/* Recent Searches */}
      {isAuthenticated && searchHistory.length > 0 && (
        <div>
          <h2 className="text-base font-semibold text-on-surface mb-3">Recent Searches</h2>
          <div className="flex flex-wrap gap-2">
            {searchHistory.slice(0, 8).map((item, idx) => (
              <button
                key={idx}
                onClick={() => onHistoryItemClick(item.merchant)}
                className="px-3.5 py-2 bg-primary-light/60 rounded-full text-sm font-medium text-primary"
              >
                {item.merchant}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Find Best Card + Results */}
      <div className="grid grid-cols-1 gap-4">
        <BentoCard>
          <h2 className="text-base font-semibold text-on-surface mb-3">
            <span className="material-symbols-outlined text-primary align-middle mr-1" style={{ fontSize: 20 }}>search</span>
            Find Best Card
          </h2>
          <MerchantLookup onLookup={onMerchantLookup} loading={loading} initialData={merchantData} />
        </BentoCard>

        {loading && <LoadingSpinner text="Finding best card offers…" />}

        {recommendations && (
          <BentoCard className="!p-0 overflow-hidden">
            <RecommendationResults data={recommendations} />
          </BentoCard>
        )}
      </div>

      {/* Rewards Bento Grid */}
      <div>
        <h2 className="text-base font-semibold text-on-surface mb-3">Rewards Overview</h2>
        <div className="grid grid-cols-2 gap-3">
          <BentoCard className="bg-gradient-to-br from-primary/5 to-primary/10">
            <span className="material-symbols-outlined text-primary mb-2" style={{ fontSize: 24 }}>speed</span>
            <p className="text-xs text-on-surface-variant">Cashback Velocity</p>
            <p className="text-2xl font-bold text-on-surface tracking-tight mt-1">$0.00</p>
            <p className="text-xs text-on-surface-variant mt-0.5">this month</p>
          </BentoCard>
          <BentoCard className="bg-gradient-to-br from-success/5 to-success/10">
            <span className="material-symbols-outlined text-success mb-2" style={{ fontSize: 24 }}>flag</span>
            <p className="text-xs text-on-surface-variant">Goal Progress</p>
            <p className="text-2xl font-bold text-on-surface tracking-tight mt-1">0%</p>
            <p className="text-xs text-on-surface-variant mt-0.5">of monthly target</p>
          </BentoCard>
        </div>
      </div>
    </div>
  );
}

export default CardsTab;
